import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Flex, Text, Input, Button, Badge, Icon, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Skeleton, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  FormControl, FormLabel, useDisclosure, VStack, HStack, Tooltip, Collapse, IconButton,
  InputGroup, InputLeftElement, Alert, AlertIcon
} from '@chakra-ui/react';
import { Card, Title } from "@tremor/react";
import {
  AlertTriangle, RefreshCw, Search, DollarSign, ChevronDown, ChevronRight,
  Package, CheckCircle2, Clock, Save
} from 'lucide-react';
import ScrollToTop from "../components/ScrollToTop";
import { getDreFuroEstoque, setDreFuroEstoqueCmv } from "../services/api";

const formatMoney = (val) => `R$ ${parseFloat(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const parts = String(dateStr).substring(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const [datePart, timePart] = String(dateStr).split(' ');
  const parts = datePart.split('-');
  const formattedDate = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : datePart;
  return timePart ? `${formattedDate} ${timePart.substring(0, 5)}` : formattedDate;
};

const DREFuroEstoque = () => {
  const toast = useToast();
  const dataFetchedRef = useRef(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  const [dataInicio, setDataInicio] = useState(primeiroDia.toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState(hoje.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [vendas, setVendas] = useState([]);
  const [busca, setBusca] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [saving, setSaving] = useState(false);

  // Modal state
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [cmvManual, setCmvManual] = useState('');

  const fetchData = async () => {
    setLoading(true);
    const params = {};
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;

    const res = await getDreFuroEstoque(params);
    if (res && res.data) {
      setVendas(res.data);
    } else if (res === false) {
      toast({ title: 'Erro ao carregar vendas furo de estoque', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!dataFetchedRef.current) {
      fetchData();
      dataFetchedRef.current = true;
    }
  }, []);

  const toggleRow = (vendaId) => {
    setExpandedRows(prev => ({ ...prev, [vendaId]: !prev[vendaId] }));
  };

  const handleOpenCmv = (venda) => {
    setSelectedVenda(venda);
    setCmvManual(venda.cmv_fonte === 'manual' && venda.cmv ? String(venda.cmv) : '');
    onOpen();
  };

  const handleSaveCmv = async () => {
    if (!cmvManual || parseFloat(cmvManual) < 0) {
      toast({ title: 'Informe um valor de CMV válido', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    const res = await setDreFuroEstoqueCmv(selectedVenda.venda_id, { cmv: parseFloat(cmvManual) });

    if (res && (res.status === 200 || res.status === 201)) {
      toast({ title: 'CMV manual definido com sucesso', status: 'success', duration: 3000 });
      onClose();
      fetchData();
    } else {
      const msg = res?.data?.message || 'Erro ao definir CMV';
      toast({ title: msg, status: 'error', duration: 3000 });
    }
    setSaving(false);
  };

  // Filtro de busca
  const filteredVendas = vendas.filter(v => {
    if (!busca) return true;
    const term = busca.toLowerCase();
    return String(v.venda_id).includes(term) ||
      (v.id_origem || '').toLowerCase().includes(term) ||
      (v.origem || '').toLowerCase().includes(term) ||
      (v.itens || []).some(i => (i.nome || '').toLowerCase().includes(term) || (i.codigo_fornecedor || '').toLowerCase().includes(term));
  });

  // Resumos
  const totalVendas = filteredVendas.length;
  const semCmv = filteredVendas.filter(v => !v.cmv || v.cmv === 0).length;
  const comCmvManual = filteredVendas.filter(v => v.cmv_fonte === 'manual').length;
  const pendentes = totalVendas - comCmvManual;

  const renderCmvBadge = (venda) => {
    if (venda.cmv_fonte === 'manual') {
      return (
        <Tooltip label="CMV definido manualmente">
          <Badge colorScheme="green" fontSize="2xs" borderRadius="full" px={2}>
            <Flex align="center" gap={1}><CheckCircle2 size={10} /> Manual</Flex>
          </Badge>
        </Tooltip>
      );
    }
    if (!venda.cmv || venda.cmv === 0) {
      return (
        <Tooltip label="CMV zerado — necessita definição manual">
          <Badge colorScheme="red" fontSize="2xs" borderRadius="full" px={2}>
            <Flex align="center" gap={1}><AlertTriangle size={10} /> Sem CMV</Flex>
          </Badge>
        </Tooltip>
      );
    }
    return (
      <Tooltip label={`CMV via ${venda.cmv_fonte} — pode não refletir custo real`}>
        <Badge colorScheme="orange" fontSize="2xs" borderRadius="full" px={2}>
          <Flex align="center" gap={1}><Clock size={10} /> {venda.cmv_fonte}</Flex>
        </Badge>
      </Tooltip>
    );
  };

  return (
    <Box className="min-h-screen bg-gray-50" pt="60px">
      <ScrollToTop />
      <Box maxW="1400px" mx="auto" px={{ base: 3, md: 6 }} py={6}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
          <Box>
            <Flex align="center" gap={2}>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">Furo de Estoque</Text>
              <Badge colorScheme="orange" fontSize="sm" borderRadius="full" px={3}>{totalVendas}</Badge>
            </Flex>
            <Text fontSize="sm" color="gray.500">Vendas compradas de fornecedores alternativos — defina o CMV manualmente</Text>
          </Box>
        </Flex>

        {/* Filtros */}
        <Card className="ring-0 mb-6">
          <Flex gap={3} flexWrap="wrap" align="end">
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Início</Text>
              <Input type="date" size="sm" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Fim</Text>
              <Input type="date" size="sm" value={dataFim} onChange={(e) => setDataFim(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box flex={1} minW="180px">
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Buscar</Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none"><Search size={14} color="gray" /></InputLeftElement>
                <Input placeholder="ID venda, produto, fornecedor..." value={busca} onChange={(e) => setBusca(e.target.value)} bg="white" borderRadius="md" />
              </InputGroup>
            </Box>
            <Button size="sm" colorScheme="blue" leftIcon={<RefreshCw size={14} />} onClick={fetchData} isLoading={loading} px={6}>
              Filtrar
            </Button>
          </Flex>
        </Card>

        {/* Resumo */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="orange.50">
                <Icon as={AlertTriangle} boxSize={5} color="orange.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Total Furos</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="orange.600">{totalVendas}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="red.50">
                <Icon as={DollarSign} boxSize={5} color="red.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Sem CMV</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="red.600">{semCmv}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="green.50">
                <Icon as={CheckCircle2} boxSize={5} color="green.500" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">CMV Manual</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="green.600">{comCmvManual}</Text>
                )}
              </Box>
            </Flex>
          </Card>
          <Card className="ring-0">
            <Flex align="center" gap={3}>
              <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg="yellow.50">
                <Icon as={Clock} boxSize={5} color="yellow.600" />
              </Flex>
              <Box>
                <Text fontSize="xs" color="gray.500">Pendentes</Text>
                {loading ? <Skeleton h="24px" w="60px" /> : (
                  <Text fontSize="lg" fontWeight="bold" color="yellow.600">{pendentes}</Text>
                )}
              </Box>
            </Flex>
          </Card>
        </SimpleGrid>

        {/* Tabela */}
        <Card className="ring-0">
          <Flex justify="space-between" align="center" mb={4}>
            <Title>Vendas com Furo de Estoque</Title>
          </Flex>

          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr bg="gray.50">
                  <Th fontSize="xs" w="30px"></Th>
                  <Th fontSize="xs">ID Venda</Th>
                  <Th fontSize="xs">ID Origem</Th>
                  <Th fontSize="xs">Origem</Th>
                  <Th fontSize="xs">Data</Th>
                  <Th fontSize="xs">Itens</Th>
                  <Th fontSize="xs" isNumeric>CMV</Th>
                  <Th fontSize="xs">Status CMV</Th>
                  <Th fontSize="xs" w="80px">Ação</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <Tr key={i}>{[...Array(9)].map((_, j) => <Td key={j}><Skeleton h="16px" w="80px" /></Td>)}</Tr>
                  ))
                ) : filteredVendas.length === 0 ? (
                  <Tr>
                    <Td colSpan={9}>
                      <Text color="gray.400" textAlign="center" py={8}>
                        Nenhuma venda com furo de estoque no período
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredVendas.map((v) => (
                    <React.Fragment key={v.venda_id}>
                      <Tr
                        _hover={{ bg: 'gray.50' }}
                        cursor="pointer"
                        onClick={() => toggleRow(v.venda_id)}
                        bg={(!v.cmv || v.cmv === 0) ? 'red.50' : undefined}
                      >
                        <Td>
                          <Icon
                            as={expandedRows[v.venda_id] ? ChevronDown : ChevronRight}
                            boxSize={4} color="gray.400"
                          />
                        </Td>
                        <Td fontSize="sm" fontWeight="medium" color="gray.800">#{v.venda_id}</Td>
                        <Td>
                          <Text fontSize="xs" color="gray.600" fontFamily="mono">{v.id_origem}</Text>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={v.origem === 'Mercado Livre' ? 'yellow' : v.origem === 'ECOMMERCE' ? 'blue' : 'purple'}
                            fontSize="2xs" borderRadius="full" px={2}
                          >
                            {v.origem}
                          </Badge>
                        </Td>
                        <Td fontSize="xs" color="gray.600">{formatDateTime(v.data_venda)}</Td>
                        <Td>
                          <Badge colorScheme="gray" fontSize="2xs" borderRadius="full" px={2}>
                            {(v.itens || []).length} {(v.itens || []).length === 1 ? 'item' : 'itens'}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" fontWeight="bold" color={v.cmv > 0 ? 'gray.800' : 'red.500'}>
                            {v.cmv != null ? formatMoney(v.cmv) : '-'}
                          </Text>
                        </Td>
                        <Td>{renderCmvBadge(v)}</Td>
                        <Td>
                          <Tooltip label="Definir CMV manual">
                            <IconButton
                              icon={<DollarSign size={14} />}
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={(e) => { e.stopPropagation(); handleOpenCmv(v); }}
                            />
                          </Tooltip>
                        </Td>
                      </Tr>
                      {/* Itens expandidos */}
                      {expandedRows[v.venda_id] && (
                        <Tr>
                          <Td colSpan={9} p={0} borderBottom="2px solid" borderColor="gray.200">
                            <Box bg="gray.50" px={8} py={3}>
                              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>
                                Itens da Venda #{v.venda_id}
                              </Text>
                              <Table size="sm" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th fontSize="2xs">Produto</Th>
                                    <Th fontSize="2xs">Cód. Fornecedor</Th>
                                    <Th fontSize="2xs" isNumeric>Qtd</Th>
                                    <Th fontSize="2xs" isNumeric>Valor Unitário</Th>
                                    <Th fontSize="2xs" isNumeric>Valor Compra</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {(v.itens || []).map((item, idx) => (
                                    <Tr key={idx}>
                                      <Td>
                                        <HStack spacing={2}>
                                          <Icon as={Package} boxSize={3} color="gray.400" />
                                          <Text fontSize="xs" fontWeight="medium" color="gray.700">{item.nome}</Text>
                                        </HStack>
                                      </Td>
                                      <Td fontSize="xs" color="gray.600" fontFamily="mono">{item.codigo_fornecedor || '-'}</Td>
                                      <Td isNumeric fontSize="xs" color="gray.700">{item.quantidade}</Td>
                                      <Td isNumeric fontSize="xs" color="gray.700">{formatMoney(item.valor_unitario)}</Td>
                                      <Td isNumeric>
                                        <Text fontSize="xs" fontWeight="medium" color={item.valor_compra > 0 ? 'gray.700' : 'red.500'}>
                                          {formatMoney(item.valor_compra)}
                                        </Text>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                          </Td>
                        </Tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Modal CMV Manual */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg" pb={2}>
            <Flex align="center" gap={2}>
              <Text>Definir CMV Manual</Text>
              <Badge colorScheme="orange" fontSize="xs" borderRadius="full" px={2}>Furo de Estoque</Badge>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedVenda && (
              <VStack spacing={4} align="stretch">
                {/* Info da venda */}
                <Box bg="gray.50" borderRadius="md" p={3}>
                  <SimpleGrid columns={2} spacing={2}>
                    <Box>
                      <Text fontSize="2xs" color="gray.500">Venda</Text>
                      <Text fontSize="sm" fontWeight="bold">#{selectedVenda.venda_id}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="2xs" color="gray.500">Origem</Text>
                      <Text fontSize="sm">{selectedVenda.origem}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="2xs" color="gray.500">Data</Text>
                      <Text fontSize="sm">{formatDateTime(selectedVenda.data_venda)}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="2xs" color="gray.500">CMV Atual</Text>
                      <Text fontSize="sm" fontWeight="bold" color={selectedVenda.cmv > 0 ? 'gray.800' : 'red.500'}>
                        {selectedVenda.cmv != null ? formatMoney(selectedVenda.cmv) : '-'}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                {/* Itens */}
                {(selectedVenda.itens || []).length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>Itens</Text>
                    {selectedVenda.itens.map((item, idx) => (
                      <Flex key={idx} justify="space-between" align="center" py={1} borderBottom="1px solid" borderColor="gray.100">
                        <Box>
                          <Text fontSize="xs" fontWeight="medium" color="gray.700">{item.nome}</Text>
                          <Text fontSize="2xs" color="gray.400">{item.codigo_fornecedor} | Qtd: {item.quantidade}</Text>
                        </Box>
                        <VStack spacing={0} align="end">
                          <Text fontSize="2xs" color="gray.400">Venda: {formatMoney(item.valor_unitario)}</Text>
                          <Text fontSize="2xs" color={item.valor_compra > 0 ? 'gray.500' : 'red.400'}>
                            Compra: {formatMoney(item.valor_compra)}
                          </Text>
                        </VStack>
                      </Flex>
                    ))}
                  </Box>
                )}

                {/* Input CMV */}
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Valor total do CMV (R$)</FormLabel>
                  <Input
                    size="sm" type="number" step="0.01" min="0"
                    placeholder="0,00"
                    value={cmvManual}
                    onChange={(e) => setCmvManual(e.target.value)}
                    autoFocus
                  />
                </FormControl>

                <Alert status="info" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  O CMV manual é protegido contra sobrescrita pelo sync automático.
                </Alert>

                {selectedVenda.cmv_fonte === 'manual' && (
                  <Alert status="warning" borderRadius="md" fontSize="xs">
                    <AlertIcon />
                    Esta venda já possui CMV manual. O valor será substituído.
                  </Alert>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
            <Button size="sm" colorScheme="blue" onClick={handleSaveCmv} isLoading={saving} leftIcon={<Save size={14} />}>
              Salvar CMV
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DREFuroEstoque;
